/**
 * Pathfinder 2e built-in character sheet template.
 * Extracted from shared/builtinTemplates.ts for system isolation.
 */

import type { SheetTemplate } from '../../types.js';

export const PF2E_TEMPLATE_ID = 'pf2e-character';

export const pf2eTemplate: SheetTemplate = {
  id: PF2E_TEMPLATE_ID,
  name: 'Pathfinder 2e',
  builtIn: true,
  sections: [
    // --- Identity ---
    {
      key: 'identity',
      label: 'Основные',
      fields: [
        { key: 'name', label: 'Имя', type: 'text', defaultValue: '' },
        { key: 'ancestry', label: 'Родословная', type: 'text', defaultValue: '' },
        { key: 'heritage', label: 'Наследие', type: 'text', defaultValue: '' },
        { key: 'class_name', label: 'Класс', type: 'text', defaultValue: '' },
        { key: 'level', label: 'Уровень', type: 'number', defaultValue: 1, min: 1, max: 20 },
        { key: 'background', label: 'Предыстория', type: 'text', defaultValue: '' },
        { key: 'xp', label: 'Опыт', type: 'number', defaultValue: 0, min: 0 },
      ],
    },
    // --- Abilities ---
    {
      key: 'abilities',
      label: 'Характеристики',
      fields: [
        { key: 'str', label: 'Сила', type: 'number', defaultValue: 10, min: 1, max: 30, rollExpression: '1d20 + str_mod' },
        { key: 'dex', label: 'Ловкость', type: 'number', defaultValue: 10, min: 1, max: 30, rollExpression: '1d20 + dex_mod' },
        { key: 'con', label: 'Телосложение', type: 'number', defaultValue: 10, min: 1, max: 30, rollExpression: '1d20 + con_mod' },
        { key: 'int', label: 'Интеллект', type: 'number', defaultValue: 10, min: 1, max: 30, rollExpression: '1d20 + int_mod' },
        { key: 'wis', label: 'Мудрость', type: 'number', defaultValue: 10, min: 1, max: 30, rollExpression: '1d20 + wis_mod' },
        { key: 'cha', label: 'Харизма', type: 'number', defaultValue: 10, min: 1, max: 30, rollExpression: '1d20 + cha_mod' },
        { key: 'str_mod', label: 'Мод. СИЛ', type: 'number', formula: 'floor((str - 10) / 2)' },
        { key: 'dex_mod', label: 'Мод. ЛОВ', type: 'number', formula: 'floor((dex - 10) / 2)' },
        { key: 'con_mod', label: 'Мод. ТЕЛ', type: 'number', formula: 'floor((con - 10) / 2)' },
        { key: 'int_mod', label: 'Мод. ИНТ', type: 'number', formula: 'floor((int - 10) / 2)' },
        { key: 'wis_mod', label: 'Мод. МДР', type: 'number', formula: 'floor((wis - 10) / 2)' },
        { key: 'cha_mod', label: 'Мод. ХАР', type: 'number', formula: 'floor((cha - 10) / 2)' },
      ],
    },
    // --- Saves ---
    {
      key: 'saves',
      label: 'Спасброски',
      fields: [
        { key: 'fort_prof_bonus', label: 'Мастерство Стойкости', type: 'number', defaultValue: 0 },
        { key: 'fort_save', label: 'Стойкость', type: 'number', formula: 'con_mod + fort_prof_bonus', rollExpression: '1d20 + fort_save' },
        { key: 'ref_prof_bonus', label: 'Мастерство Рефлекса', type: 'number', defaultValue: 0 },
        { key: 'ref_save', label: 'Рефлекс', type: 'number', formula: 'dex_mod + ref_prof_bonus', rollExpression: '1d20 + ref_save' },
        { key: 'will_prof_bonus', label: 'Мастерство Воли', type: 'number', defaultValue: 0 },
        { key: 'will_save', label: 'Воля', type: 'number', formula: 'wis_mod + will_prof_bonus', rollExpression: '1d20 + will_save' },
      ],
    },
    // --- Perception ---
    {
      key: 'perception_section',
      label: 'Восприятие',
      fields: [
        { key: 'perception_prof_bonus', label: 'Мастерство Восприятия', type: 'number', defaultValue: 0 },
        { key: 'perception', label: 'Восприятие', type: 'number', formula: 'wis_mod + perception_prof_bonus', rollExpression: '1d20 + perception' },
      ],
    },
    // --- Combat ---
    {
      key: 'combat',
      label: 'Бой',
      fields: [
        { key: 'ac_prof_bonus', label: 'Мастерство КД', type: 'number', defaultValue: 0 },
        { key: 'armor_bonus', label: 'Бонус доспеха', type: 'number', defaultValue: 0 },
        { key: 'ac', label: 'Класс доспеха', type: 'number', formula: '10 + dex_mod + ac_prof_bonus + armor_bonus' },
        { key: 'hp', label: 'Текущие хиты', type: 'number', defaultValue: 0, min: 0 },
        { key: 'max_hp', label: 'Максимальные хиты', type: 'number', defaultValue: 0, min: 0 },
        { key: 'temp_hp', label: 'Временные хиты', type: 'number', defaultValue: 0, min: 0 },
        { key: 'speed', label: 'Скорость', type: 'number', defaultValue: 25, min: 0 },
        { key: 'initiative', label: 'Инициатива', type: 'number', formula: 'perception' },
      ],
    },
    // --- Actions ---
    {
      key: 'actions',
      label: 'Действия',
      fields: [
        { key: 'actions_total', label: 'Действий за ход', type: 'number', defaultValue: 3, min: 0, max: 4 },
        { key: 'actions_used', label: 'Использовано', type: 'number', defaultValue: 0, min: 0 },
        { key: 'reaction_available', label: 'Реакция доступна', type: 'boolean', defaultValue: true },
      ],
    },
    // --- Conditions ---
    {
      key: 'conditions_section',
      label: 'Состояния',
      fields: [
        {
          key: 'conditions',
          label: 'Состояния',
          type: 'list',
          defaultValue: [],
          options: [
            'Blinded', 'Clumsy', 'Confused', 'Controlled', 'Dazzled',
            'Deafened', 'Doomed', 'Drained', 'Dying', 'Encumbered',
            'Enfeebled', 'Fascinated', 'Fatigued', 'Flat-footed', 'Fleeing',
            'Frightened', 'Grabbed', 'Immobilized', 'Invisible', 'Paralyzed',
            'Petrified', 'Prone', 'Quickened', 'Restrained', 'Sickened',
            'Slowed', 'Stunned', 'Stupefied', 'Unconscious', 'Wounded',
          ],
        },
      ],
    },
    // --- Skills ---
    {
      key: 'skills',
      label: 'Навыки',
      fields: [
        { key: 'acrobatics', label: 'Акробатика', type: 'number', defaultValue: 0, rollExpression: '1d20 + acrobatics' },
        { key: 'arcana', label: 'Аркана', type: 'number', defaultValue: 0, rollExpression: '1d20 + arcana' },
        { key: 'athletics', label: 'Атлетика', type: 'number', defaultValue: 0, rollExpression: '1d20 + athletics' },
        { key: 'crafting', label: 'Ремесло', type: 'number', defaultValue: 0, rollExpression: '1d20 + crafting' },
        { key: 'deception', label: 'Обман', type: 'number', defaultValue: 0, rollExpression: '1d20 + deception' },
        { key: 'diplomacy', label: 'Дипломатия', type: 'number', defaultValue: 0, rollExpression: '1d20 + diplomacy' },
        { key: 'intimidation', label: 'Запугивание', type: 'number', defaultValue: 0, rollExpression: '1d20 + intimidation' },
        { key: 'lore', label: 'Знания', type: 'number', defaultValue: 0, rollExpression: '1d20 + lore' },
        { key: 'medicine', label: 'Медицина', type: 'number', defaultValue: 0, rollExpression: '1d20 + medicine' },
        { key: 'nature', label: 'Природа', type: 'number', defaultValue: 0, rollExpression: '1d20 + nature' },
        { key: 'occultism', label: 'Оккультизм', type: 'number', defaultValue: 0, rollExpression: '1d20 + occultism' },
        { key: 'performance', label: 'Представление', type: 'number', defaultValue: 0, rollExpression: '1d20 + performance' },
        { key: 'religion', label: 'Религия', type: 'number', defaultValue: 0, rollExpression: '1d20 + religion' },
        { key: 'society', label: 'Общество', type: 'number', defaultValue: 0, rollExpression: '1d20 + society' },
        { key: 'stealth', label: 'Скрытность', type: 'number', defaultValue: 0, rollExpression: '1d20 + stealth' },
        { key: 'survival', label: 'Выживание', type: 'number', defaultValue: 0, rollExpression: '1d20 + survival' },
        { key: 'thievery', label: 'Воровство', type: 'number', defaultValue: 0, rollExpression: '1d20 + thievery' },
      ],
    },
    // --- Notes ---
    {
      key: 'notes',
      label: 'Заметки',
      fields: [
        { key: 'notes', label: 'Заметки', type: 'text', defaultValue: '' },
        { key: 'languages', label: 'Языки', type: 'text', defaultValue: '' },
        { key: 'senses', label: 'Чувства', type: 'text', defaultValue: '' },
      ],
    },
    // --- Attacks (repeating) ---
    {
      key: 'attacks',
      label: 'Атаки',
      repeating: true,
      fields: [
        { key: 'name', label: 'Название', type: 'text', defaultValue: '' },
        { key: 'to_hit', label: 'Бонус атаки', type: 'number', defaultValue: 0, rollExpression: '1d20 + to_hit' },
        { key: 'damage_dice', label: 'Кости урона', type: 'text', defaultValue: '1d6' },
        { key: 'damage_type', label: 'Тип урона', type: 'select', defaultValue: 'slashing', options: ['slashing', 'piercing', 'bludgeoning', 'fire', 'cold', 'electricity', 'sonic', 'poison', 'acid', 'mental', 'force', 'positive', 'negative', 'void', 'vitality'] },
        { key: 'traits', label: 'Черты', type: 'text', defaultValue: '' },
      ],
    },
    // --- Inventory (repeating) ---
    {
      key: 'inventory',
      label: 'Инвентарь',
      repeating: true,
      fields: [
        { key: 'name', label: 'Название', type: 'text', defaultValue: '' },
        { key: 'quantity', label: 'Количество', type: 'number', defaultValue: 1, min: 0 },
        { key: 'bulk', label: 'Объём', type: 'text', defaultValue: '' },
        { key: 'description', label: 'Описание', type: 'text', defaultValue: '' },
        { key: 'invested', label: 'Инвестировано', type: 'boolean', defaultValue: false },
      ],
    },
  ],
  tokenBinding: {
    hp: 'hp',
    maxHp: 'max_hp',
    ac: 'ac',
    conditions: 'conditions',
  },
};
